/**
 * Pure builder for the role/permission matrix.
 *
 * Turns a role's saved grants (RolePermission rows with Allowed=1) + denies (Allowed=0) — plus any pending
 * in-flight edits — into the tri-state grid the view renders, and turns edits back into the per-cell save
 * actions. A cell is `on` (granted), `off` (denied), or `none` (no record → inherit/default), cycling in
 * that order, exactly like the home app's `PermissionsManager`. No pict / DOM / network dependencies.
 *
 * The "edits" object is a sparse `{ '<hash>': 'on'|'off'|'none' }` map holding ONLY cells whose state
 * differs from what is saved — so it doubles as the batched save set (`diffChanges`). Setting a cell back to
 * its saved state removes it from the map.
 */

const Catalog = require('./Permission-Catalog.js');

// The tri-state cycle order — matches the home app's `toggleStatus = ['none', 'on', 'off']`.
const TRI_STATES = [ 'none', 'on', 'off' ];

// --- Catalog sources -------------------------------------------------------
// The matrix renders one of two catalogs behind the SAME tri-state machinery: the STATIC entity/module/
// association catalog (Permission-Catalog) or a DYNAMIC reports/forms catalog built from the live
// ReportNamedInstance set. A "catalog source" is the small interface buildRows()/applyAllToggle() need —
// the action columns, the row descriptors ({ Key, Label, Kind }), per-row action membership, hash builder,
// and section titles. `Key` is the permission-hash prefix (an entity name, or `DocumentType-<RNI.Hash>`);
// `Label` is what the row shows. The provider holds the active source and routes its lookups through it.

/**
 * The static entity/module/association catalog source (the default — preserves the original behavior).
 * @returns {Object}
 */
function staticCatalogSource()
{
	return {
		Mode: 'entities',
		actions: () => Catalog.AVAILABLE_ACTIONS,
		entities: () => Catalog.entityList().map((pEntity) => ({ Key: pEntity, Label: pEntity, Kind: Catalog.rowKind(pEntity) })),
		actionsForKey: (pKey) => Catalog.actionsForEntity(pKey),
		hasAction: (pKey, pAction) => Catalog.entityHasAction(pKey, pAction),
		buildHash: (pKey, pAction) => Catalog.buildHash(pKey, pAction),
		sections: () => [ { Key: 'entity', Title: 'Entities' }, { Key: 'module', Title: 'Module access' }, { Key: 'association', Title: 'Associations' } ],
	};
}

// The base report/form actions every ReportNamedInstance declares (before per-report workflow statuses).
const REPORT_BASE_ACTIONS = [ 'Create', 'View', 'Update', 'Delete' ];

/**
 * A dynamic reports/forms catalog source built from ReportNamedInstance records. Each report/form becomes a
 * `DocumentType-<Hash>` row declaring Create/View/Update/Delete + one `View-<Status>` per approval-workflow
 * state — exactly the row set the home app's applyDocPermissions() injects (PermissionsManager.vue).
 * @param {Array<{ Hash: string, Name: string }>} pReportNamedInstances
 * @param {Object} [pStatusesByHash] - { '<RNI.Hash>': ['Draft','Approved', ...] } workflow states per report
 * @returns {Object}
 */
function reportCatalogSource(pReportNamedInstances, pStatusesByHash)
{
	const tmpStatuses = pStatusesByHash || {};
	const tmpPerKey = {};
	const tmpColumns = REPORT_BASE_ACTIONS.slice();
	(pReportNamedInstances || []).forEach((pRNI) =>
	{
		if (!pRNI || !pRNI.Hash)
		{
			return;
		}
		const tmpKey = `${Catalog.REPORT_ENTITY_PREFIX}${pRNI.Hash}`;
		const tmpStatusActions = (tmpStatuses[pRNI.Hash] || []).map((pStatus) => `View-${pStatus}`);
		tmpStatusActions.forEach((pAction) =>
		{
			if (tmpColumns.indexOf(pAction) < 0)
			{
				tmpColumns.push(pAction);
			}
		});
		tmpPerKey[tmpKey] = { Label: pRNI.Name || pRNI.Hash, Actions: REPORT_BASE_ACTIONS.concat(tmpStatusActions) };
	});
	return {
		Mode: 'reports',
		actions: () => tmpColumns,
		entities: () => Object.keys(tmpPerKey).map((pKey) => ({ Key: pKey, Label: tmpPerKey[pKey].Label, Kind: 'report' })),
		actionsForKey: (pKey) => (tmpPerKey[pKey] ? tmpPerKey[pKey].Actions : []),
		hasAction: (pKey, pAction) => (tmpPerKey[pKey] ? tmpPerKey[pKey].Actions.indexOf(pAction) > -1 : false),
		buildHash: (pKey, pAction) => `${pKey}-${pAction}`,
		sections: () => [ { Key: 'report', Title: 'Reports & Forms' } ],
	};
}

/**
 * Coerce an array or Set of hashes to a Set.
 * @param {Set<string>|Array<string>} pValue
 * @returns {Set<string>}
 */
function toSet(pValue)
{
	return (pValue instanceof Set) ? pValue : new Set(pValue || []);
}

/**
 * The state a cell would have from saved data alone (ignoring edits).
 * @param {string} pHash
 * @param {Set<string>} pGrant
 * @param {Set<string>} pDeny
 * @returns {'on'|'off'|'none'}
 */
function savedState(pHash, pGrant, pDeny)
{
	// Coerce so callers can pass arrays or Sets (e.g. the provider's "All" toggle passes the raw hash arrays).
	const tmpGrant = toSet(pGrant);
	const tmpDeny = toSet(pDeny);
	if (tmpGrant.has(pHash))
	{
		return 'on';
	}
	if (tmpDeny.has(pHash))
	{
		return 'off';
	}
	return 'none';
}

/**
 * The effective state of a cell = its edit if present, else its saved state.
 * @param {string} pHash
 * @param {Set<string>} pGrant
 * @param {Set<string>} pDeny
 * @param {Object} pEdits
 * @returns {'on'|'off'|'none'}
 */
function effectiveState(pHash, pGrant, pDeny, pEdits)
{
	if (pEdits && Object.prototype.hasOwnProperty.call(pEdits, pHash))
	{
		return pEdits[pHash];
	}
	return savedState(pHash, pGrant, pDeny);
}

/**
 * Compute the per-entity "All" meta-toggle state: `on` only if every available cell is on, `off` only if
 * every available cell is off, otherwise `none` (mixed / indeterminate). Mirrors `computeMetaToggleState`.
 * @param {Array<{ Available: boolean, State: string }>} pCells
 * @returns {'on'|'off'|'none'}
 */
function metaState(pCells)
{
	const tmpAvailable = (pCells || []).filter((pCell) => pCell.Available);
	if (!tmpAvailable.length)
	{
		return 'none';
	}
	const tmpCounts = { on: 0, off: 0, none: 0 };
	tmpAvailable.forEach((pCell) =>
	{
		tmpCounts[pCell.State] = (tmpCounts[pCell.State] || 0) + 1;
	});
	if (tmpCounts.on && !tmpCounts.off && !tmpCounts.none)
	{
		return 'on';
	}
	if (tmpCounts.off && !tmpCounts.on && !tmpCounts.none)
	{
		return 'off';
	}
	return 'none';
}

/**
 * The next state in the tri-state cycle (none -> on -> off -> none).
 * @param {string} pState
 * @returns {'on'|'off'|'none'}
 */
function cycleState(pState)
{
	const tmpIndex = TRI_STATES.indexOf(pState);
	return TRI_STATES[(tmpIndex + 1) % TRI_STATES.length];
}

/**
 * Write a cell's target state into an edits map, normalizing: if the target equals the saved state the cell
 * is no longer a change, so its key is removed (keeping `edits` == the minimal save set).
 * @param {Object} pEdits - mutated in place
 * @param {string} pHash
 * @param {string} pState
 * @param {Set<string>} pGrant
 * @param {Set<string>} pDeny
 * @returns {Object} the same edits object
 */
function setEdit(pEdits, pHash, pState, pGrant, pDeny)
{
	if (pState === savedState(pHash, pGrant, pDeny))
	{
		delete pEdits[pHash];
	}
	else
	{
		pEdits[pHash] = pState;
	}
	return pEdits;
}

/**
 * Build the matrix model: the action columns + a row per catalog entity with a tri-state cell for each
 * action (or an unavailable placeholder where the entity doesn't declare that action).
 * @param {Set<string>|Array<string>} pGrantHashes - hashes saved Allowed=1
 * @param {Set<string>|Array<string>} pDenyHashes - hashes saved Allowed=0
 * @param {Object} [pEdits] - sparse pending edits { '<hash>':'on'|'off'|'none' }
 * @param {Object} [pCatalogSource] - a catalog source (defaults to the static entity catalog)
 * @returns {{ actions: Array<string>, rows: Array<object> }}
 */
function buildRows(pGrantHashes, pDenyHashes, pEdits, pCatalogSource)
{
	const tmpGrant = toSet(pGrantHashes);
	const tmpDeny = toSet(pDenyHashes);
	const tmpEdits = pEdits || {};
	const tmpSource = pCatalogSource || staticCatalogSource();
	const tmpActions = tmpSource.actions();

	const tmpRows = tmpSource.entities().map((pEntity) =>
	{
		const tmpSupported = tmpSource.actionsForKey(pEntity.Key);
		const tmpCells = tmpActions.map((pAction) =>
		{
			const tmpHash = tmpSource.buildHash(pEntity.Key, pAction);
			const tmpAvailable = tmpSupported.indexOf(pAction) > -1;
			const tmpSaved = savedState(tmpHash, tmpGrant, tmpDeny);
			const tmpState = effectiveState(tmpHash, tmpGrant, tmpDeny, tmpEdits);
			return {
				Hash: tmpHash,
				Action: pAction,
				Available: tmpAvailable,
				State: tmpState,
				SavedState: tmpSaved,
				Changed: tmpAvailable && (tmpState !== tmpSaved),
			};
		});
		return {
			Entity: pEntity.Label,
			Key: pEntity.Key,
			Kind: pEntity.Kind,
			Cells: tmpCells,
			MetaState: metaState(tmpCells),
		};
	});

	return { actions: tmpActions, rows: tmpRows };
}

/**
 * Cycle a single cell to its next tri-state, returning a NEW edits map.
 * @param {string} pHash
 * @param {Set<string>|Array<string>} pGrantHashes
 * @param {Set<string>|Array<string>} pDenyHashes
 * @param {Object} [pEdits]
 * @returns {Object} the new edits map
 */
function toggleCell(pHash, pGrantHashes, pDenyHashes, pEdits)
{
	const tmpGrant = toSet(pGrantHashes);
	const tmpDeny = toSet(pDenyHashes);
	const tmpEdits = Object.assign({}, pEdits || {});
	const tmpNext = cycleState(effectiveState(pHash, tmpGrant, tmpDeny, tmpEdits));
	return setEdit(tmpEdits, pHash, tmpNext, tmpGrant, tmpDeny);
}

/**
 * Set every available cell of an entity to a target state ("All" toggle), returning a NEW edits map.
 * @param {string} pKey - the row key (an entity name, or `DocumentType-<RNI.Hash>`)
 * @param {string} pTargetState - 'on'|'off'|'none'
 * @param {Set<string>|Array<string>} pGrantHashes
 * @param {Set<string>|Array<string>} pDenyHashes
 * @param {Object} [pEdits]
 * @param {Object} [pCatalogSource] - a catalog source (defaults to the static entity catalog)
 * @returns {Object} the new edits map
 */
function applyAllToggle(pKey, pTargetState, pGrantHashes, pDenyHashes, pEdits, pCatalogSource)
{
	const tmpGrant = toSet(pGrantHashes);
	const tmpDeny = toSet(pDenyHashes);
	const tmpEdits = Object.assign({}, pEdits || {});
	const tmpSource = pCatalogSource || staticCatalogSource();
	tmpSource.actionsForKey(pKey).forEach((pAction) =>
	{
		setEdit(tmpEdits, tmpSource.buildHash(pKey, pAction), pTargetState, tmpGrant, tmpDeny);
	});
	return tmpEdits;
}

/**
 * The batched save set — a shallow copy of the (already-minimal) edits map. Each entry is a per-cell save
 * action the provider executes: `on` -> Grant, `off` -> Deny, `none` -> delete the existing RolePermission row.
 * @param {Object} [pEdits]
 * @returns {Object}
 */
function diffChanges(pEdits)
{
	return Object.assign({}, pEdits || {});
}

/**
 * Collapse raw RolePermission rows to one per Hash (last wins — guards against accidental duplicate rows,
 * which the schema has no unique constraint to prevent — plan risk #3).
 * @param {Array<{ Hash: string }>} pRows
 * @returns {{ byHash: Object, hashes: Array<string> }}
 */
function dedupRolePermissionRows(pRows)
{
	const tmpByHash = {};
	(pRows || []).forEach((pRow) =>
	{
		if (pRow && pRow.Hash)
		{
			tmpByHash[pRow.Hash] = pRow;
		}
	});
	return { byHash: tmpByHash, hashes: Object.keys(tmpByHash) };
}

module.exports =
{
	TRI_STATES,
	REPORT_BASE_ACTIONS,
	staticCatalogSource,
	reportCatalogSource,
	savedState,
	effectiveState,
	metaState,
	cycleState,
	setEdit,
	buildRows,
	toggleCell,
	applyAllToggle,
	diffChanges,
	dedupRolePermissionRows,
};
